import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, adminHeaders } from "@/lib/api";
import { toast } from "sonner";
import { X, Trash2, Loader2, CalendarRange, Tag, FilePlus2, Filter, FileDown, List, ChevronLeft, ChevronRight } from "lucide-react";

const STATUS = {
  todo: { label: "Da fare", cls: "bg-[#334155]/60 text-[#cbd5e1]", dot: "#64748B" },
  in_progress: { label: "In corso", cls: "bg-[#F59E0B]/20 text-[#fbbf24]", dot: "#F59E0B" },
  done: { label: "Fatto", cls: "bg-[#10B981]/20 text-[#34d399]", dot: "#10B981" },
};
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const ideaTitle = (item) => { const i = item.idea || {}; return i.title || i.title_it || i.keyword || i.keyword_it || "Idea"; };

export default function ContentPlan({ onClose, onCreateArticle }) {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [tenantFilter, setTenantFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState("list");
  const [dragId, setDragId] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/admin/content-plan`, { headers: adminHeaders() }),
        axios.get(`${API}/admin/clients`, { headers: adminHeaders() }).catch(() => ({ data: [] })),
      ]);
      setItems(p.data || []);
      setClients(c.data || []);
    } catch { toast.error("Caricamento piano fallito"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const clientName = (t) => clients.find((c) => c.tenant === t)?.name || t || "Spectraloom";
  const del = async (id) => {
    if (!window.confirm("Eliminare questa voce dal piano?")) return;
    try { await axios.delete(`${API}/admin/content-plan/${id}`, { headers: adminHeaders() }); setItems((xs) => xs.filter((x) => x.id !== id)); toast.success("Voce eliminata"); }
    catch { toast.error("Eliminazione fallita"); }
  };
  const patch = async (id, body) => {
    try { const r = await axios.patch(`${API}/admin/content-plan/${id}`, body, { headers: adminHeaders() }); setItems((xs) => xs.map((x) => (x.id === id ? r.data : x))); }
    catch { toast.error("Aggiornamento fallito"); }
  };

  const shown = tenantFilter ? items.filter((x) => x.tenant === tenantFilter) : items;

  const exportPdf = async () => {
    if (!shown.length) { toast.error("Nessuna idea da esportare"); return; }
    setExporting(true);
    try {
      const r = await axios.post(`${API}/admin/content-plan/report`,
        { tenant: tenantFilter || null, client_name: tenantFilter ? clientName(tenantFilter) : "" },
        { headers: adminHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `piano-editoriale-${tenantFilter ? clientName(tenantFilter).toLowerCase().replace(/[^a-z0-9]+/g, "-") : "tutti"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF del piano scaricato");
    } catch { toast.error("Esportazione PDF fallita"); }
    finally { setExporting(false); }
  };

  const GoldVol = ({ gold, vol, label, unit = "/mese" }) => (
    <div className="flex items-center gap-1.5">
      {label ? <span className="text-[9px] font-mono font-bold text-[#8B5CF6]">{label}</span> : null}
      <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-[#10B981]/20 text-[#34d399]" title="ABSIO Punto d'oro">★ {gold ?? 0}</span>
      <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${(vol || 0) > 0 ? "bg-[#06B6D4]/20 text-[#a5f3fc]" : "bg-[#334155]/40 text-[#94A3B8]"}`}>{vol != null ? `${vol}${unit}` : "stima AI"}</span>
    </div>
  );

  const StatusDate = ({ item }) => (
    <div className="flex items-center gap-2 flex-wrap">
      <select data-testid={`plan-status-${item.id}`} value={item.status || "todo"} onChange={(e) => patch(item.id, { status: e.target.value })}
        className={`text-[11px] font-semibold rounded-full px-2 py-1 border-0 focus:outline-none cursor-pointer ${STATUS[item.status || "todo"].cls}`}>
        {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      <input type="date" data-testid={`plan-date-${item.id}`} value={item.publish_date || ""} onChange={(e) => patch(item.id, { publish_date: e.target.value })}
        className="text-[11px] rounded-lg bg-[#0B0D12] border border-[#2D3748] px-2 py-1 text-[#cbd5e1] focus:outline-none focus:border-[#8B5CF6]" />
    </div>
  );

  // ---- Calendar helpers ----
  const monthItems = shown.filter((x) => (x.publish_date || "").startsWith(`${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}`));
  const unscheduled = shown.filter((x) => !(x.publish_date || "").trim());
  const byDay = {};
  monthItems.forEach((x) => { (byDay[x.publish_date] = byDay[x.publish_date] || []).push(x); });
  const first = new Date(cursor.y, cursor.m, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const todayStr = ymd(new Date());
  const shiftMonth = (delta) => setCursor((c) => { const nm = c.m + delta; const y = c.y + Math.floor(nm / 12); const m = ((nm % 12) + 12) % 12; return { y, m }; });

  const dropOnDate = (dateStr) => { const id = dragId; setDragId(null); setDragOverDate(null); if (id) patch(id, { publish_date: dateStr }); };
  const dropUnschedule = () => { const id = dragId; setDragId(null); setDragOverDate(null); if (id) patch(id, { publish_date: "" }); };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="content-plan-modal">
      <div className="bg-[#121620] border border-[#2D3748] rounded-2xl w-full max-w-4xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2D3748]">
          <div className="flex items-center gap-2"><CalendarRange className="w-5 h-5 text-[#10B981]" /><h2 className="text-lg font-bold text-white">Piano editoriale</h2><span className="text-xs text-[#64748B]">{items.length} idee salvate</span></div>
          <button data-testid="close-plan" onClick={onClose} className="text-[#94A3B8] hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 pt-4 flex items-center gap-2 flex-wrap">
          <div className="flex items-center rounded-full border border-[#2D3748] p-1 bg-[#0B0D12]">
            <button data-testid="plan-view-list" onClick={() => setView("list")} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${view === "list" ? "bg-[#8B5CF6] text-white" : "text-[#94A3B8] hover:text-white"}`}><List className="w-3.5 h-3.5" /> Lista</button>
            <button data-testid="plan-view-calendar" onClick={() => setView("calendar")} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${view === "calendar" ? "bg-[#8B5CF6] text-white" : "text-[#94A3B8] hover:text-white"}`}><CalendarRange className="w-3.5 h-3.5" /> Calendario</button>
          </div>
          <Filter className="w-4 h-4 text-[#64748B] ml-1" />
          <select data-testid="plan-client-filter" value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)}
            className="bg-[#0B0D12] border border-[#2D3748] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-[#10B981]">
            <option value="">Tutti i clienti</option>
            {clients.map((c) => <option key={c.tenant} value={c.tenant}>{c.name}{c.is_default ? " (predefinito)" : ""}</option>)}
          </select>
          <button type="button" data-testid="plan-export-pdf" onClick={exportPdf} disabled={exporting || !shown.length}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#8B5CF6] text-white text-xs font-semibold hover:bg-[#7C3AED] transition-colors disabled:opacity-50">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Esporta PDF white-label
          </button>
        </div>

        {loading && <div className="p-6"><p className="text-sm text-[#94A3B8] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</p></div>}
        {!loading && !shown.length && <div className="p-6"><p className="text-sm text-[#64748B]">Nessuna idea nel piano. Usa "Suggerisci argomenti" e premi "+ Piano" per salvarle qui.</p></div>}

        {!loading && !!shown.length && view === "list" && (
          <div className="p-6 space-y-2" data-testid="plan-list">
            {shown.map((item) => {
              const idea = item.idea || {};
              const bi = !!idea.bilingual;
              return (
                <div key={item.id} data-testid={`plan-item-${item.id}`} className="rounded-lg border border-[#2D3748] bg-[#0B0D12] p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      {bi ? (
                        <div className="space-y-1.5">
                          <div>
                            <p className="text-sm font-semibold text-[#e2e8f0]"><span className="text-[9px] font-mono font-bold text-[#8B5CF6] mr-1">IT</span>{idea.title_it}</p>
                            <p className="text-[11px] text-[#67e8f9]"><Tag className="w-3 h-3 inline mr-1" />{idea.keyword_it}</p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-[#e2e8f0]"><span className="text-[9px] font-mono font-bold text-[#06B6D4] mr-1">EN</span>{idea.title_en}</p>
                            <p className="text-[11px] text-[#67e8f9]"><Tag className="w-3 h-3 inline mr-1" />{idea.keyword_en}</p>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm font-semibold text-[#e2e8f0]">{idea.title}</p>
                          <p className="text-[11px] text-[#67e8f9]"><Tag className="w-3 h-3 inline mr-1" />{idea.keyword}</p>
                        </div>
                      )}
                      <p className="text-[10px] text-[#64748B] mt-1.5">{clientName(item.tenant)}{bi ? " · bilingue" : ""}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      {bi ? (
                        <>
                          <GoldVol gold={idea.gold_it} vol={idea.volume_it} label="IT" />
                          <GoldVol gold={idea.gold_en} vol={idea.volume_en} label="EN" unit="/mo" />
                        </>
                      ) : (
                        <GoldVol gold={idea.gold} vol={idea.search_volume} />
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-[#1e2533] flex-wrap">
                    <StatusDate item={item} />
                    <div className="flex items-center gap-2">
                      <button type="button" data-testid={`plan-create-${item.id}`} onClick={() => onCreateArticle(item)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#8B5CF6] text-white text-xs font-semibold hover:bg-[#7C3AED] transition-colors">
                        <FilePlus2 className="w-3.5 h-3.5" /> Crea articolo
                      </button>
                      <button type="button" data-testid={`plan-del-${item.id}`} onClick={() => del(item.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#EF4444]/40 text-[#fca5a5] text-xs font-semibold hover:bg-[#EF4444]/10 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" /> Elimina
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && !!shown.length && view === "calendar" && (
          <div className="p-6" data-testid="plan-calendar">
            <div className="flex items-center justify-between mb-3">
              <button data-testid="cal-prev" onClick={() => shiftMonth(-1)} className="p-1.5 rounded-lg border border-[#2D3748] text-[#94A3B8] hover:text-white hover:border-[#8B5CF6]"><ChevronLeft className="w-4 h-4" /></button>
              <div className="text-sm font-bold text-white">{MONTHS[cursor.m]} {cursor.y}</div>
              <button data-testid="cal-next" onClick={() => shiftMonth(1)} className="p-1.5 rounded-lg border border-[#2D3748] text-[#94A3B8] hover:text-white hover:border-[#8B5CF6]"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WEEKDAYS.map((w) => <div key={w} className="text-[10px] font-semibold text-[#64748B] text-center uppercase">{w}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => {
                if (d === null) return <div key={`e${i}`} className="min-h-[72px] rounded-lg bg-transparent" />;
                const dateStr = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                const dayItems = byDay[dateStr] || [];
                const isToday = dateStr === todayStr;
                return (
                  <div key={dateStr} data-testid={`cal-day-${dateStr}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOverDate(dateStr); }}
                    onDragLeave={() => setDragOverDate((p) => (p === dateStr ? null : p))}
                    onDrop={(e) => { e.preventDefault(); dropOnDate(dateStr); }}
                    className={`min-h-[72px] rounded-lg border p-1 transition-colors ${dragOverDate === dateStr ? "border-[#10B981] bg-[#10B981]/10" : isToday ? "border-[#8B5CF6] bg-[#8B5CF6]/5" : "border-[#2D3748] bg-[#0B0D12]"}`}>
                    <div className={`text-[10px] font-mono mb-0.5 ${isToday ? "text-[#a78bfa] font-bold" : "text-[#64748B]"}`}>{d}</div>
                    <div className="space-y-0.5">
                      {dayItems.map((it) => (
                        <button key={it.id} data-testid={`cal-item-${it.id}`}
                          draggable onDragStart={(e) => { setDragId(it.id); e.dataTransfer.effectAllowed = "move"; }} onDragEnd={() => { setDragId(null); setDragOverDate(null); }}
                          onClick={() => onCreateArticle(it)} title={`${ideaTitle(it)} — ${STATUS[it.status || "todo"].label} (trascina per spostare · clic per creare articolo)`}
                          className="w-full text-left flex items-center gap-1 rounded px-1 py-0.5 bg-[#121620] hover:bg-[#1e2533] transition-colors cursor-grab active:cursor-grabbing">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: STATUS[it.status || "todo"].dot }} />
                          <span className="text-[9px] text-[#cbd5e1] truncate">{ideaTitle(it)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-3 mt-3">
              {Object.values(STATUS).map((s) => (
                <span key={s.label} className="inline-flex items-center gap-1 text-[10px] text-[#94A3B8]"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.dot }} /> {s.label}</span>
              ))}
            </div>

            {unscheduled.length > 0 && (
              <div className="mt-5" data-testid="plan-unscheduled"
                onDragOver={(e) => { e.preventDefault(); }} onDrop={(e) => { e.preventDefault(); dropUnschedule(); }}>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8] mb-2">Non pianificate ({unscheduled.length}) — assegna una data o trascina nel calendario</p>
                <div className="space-y-1.5">
                  {unscheduled.map((item) => (
                    <div key={item.id} data-testid={`plan-unsched-${item.id}`}
                      draggable onDragStart={(e) => { setDragId(item.id); e.dataTransfer.effectAllowed = "move"; }} onDragEnd={() => { setDragId(null); setDragOverDate(null); }}
                      className="flex items-center justify-between gap-2 rounded-lg border border-[#2D3748] bg-[#0B0D12] px-3 py-2 flex-wrap cursor-grab active:cursor-grabbing hover:border-[#8B5CF6]/50 transition-colors">
                      <span className="text-[12px] text-[#e2e8f0] truncate flex-1 min-w-0">{ideaTitle(item)}</span>
                      <input type="date" data-testid={`plan-date-${item.id}`} value={item.publish_date || ""} onChange={(e) => patch(item.id, { publish_date: e.target.value })}
                        className="text-[11px] rounded-lg bg-[#121620] border border-[#2D3748] px-2 py-1 text-[#cbd5e1] focus:outline-none focus:border-[#8B5CF6]" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
